'use client';

import { c as _c } from "react/compiler-runtime";
import { create } from 'zustand';
type PrV2DecisionVisibilityStore = {
  /** Decision gutter markers on screen. A line counts as on screen while any of its markers is. */
  visibleMarkers: ReadonlySet<Element>;
};

// Only the group card reads this, so scrolling past markers re-renders the card and not the diff.
const usePrV2DecisionVisibilityStore = create<PrV2DecisionVisibilityStore>(() => ({
  visibleMarkers: new Set()
}));

// Holds only observed markers, so an entry delivered after unobserve is ignored.
const lineKeyByMarker = new WeakMap<Element, string>();
let markerObserver: IntersectionObserver | null = null;
function getPrV2DecisionLineKey(filename: string, lineStart: number): string {
  return `${filename}:${lineStart}`;
}
function updateVisibleMarkers(change: (visibleMarkers: Set<Element>) => void) {
  usePrV2DecisionVisibilityStore.setState(state => {
    const visibleMarkers = new Set(state.visibleMarkers);
    change(visibleMarkers);
    return {
      visibleMarkers
    };
  });
}
function handleMarkerIntersections(entries: IntersectionObserverEntry[]) {
  updateVisibleMarkers(visibleMarkers => {
    for (const entry of entries) {
      if (!lineKeyByMarker.has(entry.target)) {
        continue;
      }
      if (entry.isIntersecting) {
        visibleMarkers.add(entry.target);
      } else {
        visibleMarkers.delete(entry.target);
      }
    }
  });
}

/** Follows whether a decision's gutter marker is on screen, for the group card to light it up. */
export function observePrV2DecisionMarker(marker: Element, filename: string, lineStart: number): void {
  // Where the browser has no IntersectionObserver, the card's questions just stay unlit.
  if (typeof IntersectionObserver === 'undefined') {
    return;
  }
  markerObserver ??= new IntersectionObserver(handleMarkerIntersections);
  lineKeyByMarker.set(marker, getPrV2DecisionLineKey(filename, lineStart));
  markerObserver.observe(marker);
}
export function unobservePrV2DecisionMarker(marker: Element): void {
  markerObserver?.unobserve(marker);
  lineKeyByMarker.delete(marker);
  if (usePrV2DecisionVisibilityStore.getState().visibleMarkers.has(marker)) {
    updateVisibleMarkers(visibleMarkers => visibleMarkers.delete(marker));
  }
}
export function useIsPrV2DecisionLineOnScreen(filename, lineStart) {
  const $ = _c(3);
  let t0;
  if ($[0] !== filename || $[1] !== lineStart) {
    t0 = state => {
      if (lineStart === null) {
        return false;
      }
      const lineKey = getPrV2DecisionLineKey(filename, lineStart);
      for (const marker of state.visibleMarkers) {
        if (lineKeyByMarker.get(marker) === lineKey) {
          return true;
        }
      }
      return false;
    };
    $[0] = filename;
    $[1] = lineStart;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return usePrV2DecisionVisibilityStore(t0);
}